import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState, store } from "..";
import { Order } from "~/types/order";
import { orderService } from "~/services";
import { observeStoreChange } from "../utils";
import { ServerLocation } from "~/types/serverLocation";


type initState = {
    loading: boolean;
    pagination: {
        limit: number;
        offset: number;
    };
    data: Order[];
    totalPages: number;
    current: Order | null;
    open: boolean;

    serverLocations: {
        vmcGCP: ServerLocation[];
        vmcAWS: ServerLocation[];
        vmcAZURE: ServerLocation[];
        edgeGCP: ServerLocation[];
        edgeAWS: ServerLocation[];
        edgeAZURE: ServerLocation[];
    }
}

const initialState: initState = {
    loading: false,
    data: [],
    pagination: {
        limit: 25,
        offset: 1,
    },
    totalPages: 0,
    current: null,
    open: false,

    serverLocations: {
        vmcGCP: [],
        vmcAWS: [],
        vmcAZURE: [],
        edgeGCP: [],
        edgeAWS: [],
        edgeAZURE: [],
    }
}

export const fetchOrders = createAsyncThunk(`order/fetchOrders`, async (_, thunk) => {
    const { limit, offset } = (thunk.getState() as RootState).order.pagination;
    try {
        const data = await orderService.getOrders(limit, offset);
        return data;
    } catch (error) {
        console.error(error);
        throw error;
    }
});

export const fetchServerLocations = createAsyncThunk(`order/fetchServerLocations`, async () => {
    try {
        const data = await orderService.getLocations();
        return data;
    } catch (error) {
        console.error(error);
        throw error;
    }
});

const orderSlice = createSlice({
    initialState,
    name: 'order',
    reducers: {
        setCurrent: (state, action: PayloadAction<Order>) => {
            state.current = action.payload;
        },
        setOpen: (state, action: PayloadAction<boolean>) => {
            state.open = action.payload;
        },
        setPagination: (state, action: PayloadAction<initState['pagination']>) => {
            state.pagination = action.payload;
        },
    },

    extraReducers: (builder) => {

        builder.addCase(fetchOrders.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload.data;
            state.totalPages = action.payload.total;
        });
        builder.addCase(fetchOrders.rejected, (state) => {
            state.loading = false;
        });
        builder.addCase(fetchOrders.pending, (state) => {
            state.loading = true;
        });

        builder.addCase(fetchServerLocations.fulfilled, (state, action) => {
            const locations = {
                vmcAWS: [] as ServerLocation[],
                vmcGCP: [] as ServerLocation[],
                vmcAZURE: [] as ServerLocation[],
                edgeAWS: [] as ServerLocation[],
                edgeGCP: [] as ServerLocation[],
                edgeAZURE: [] as ServerLocation[]
            };

            action.payload
                .filter((location: ServerLocation) => location.enabled)
                .forEach((location: ServerLocation) => {
                    const { locationType, vmcEnabled, edgeEnabled } = location;

                    const vmcKey = `vmc${locationType}` as keyof typeof locations;
                    const edgeKey = `edge${locationType}` as keyof typeof locations;
                    
                    if (vmcEnabled && locations[vmcKey]) {
                        (locations[vmcKey] as ServerLocation[]).push(location);
                    }
                    if (edgeEnabled && locations[edgeKey]) {
                        (locations[edgeKey] as ServerLocation[]).push(location);
                    }
                });

            state.serverLocations = locations;
        });
    }
});

export const orderActions = orderSlice.actions;

setTimeout(() => {
    observeStoreChange(store, (state) => state.order.pagination, (newState) => {
        store.dispatch(fetchOrders());
    });
}, 100);

export default orderSlice.reducer;

export const OrderData = (state: RootState) => state.order.data;
export const OrderLoading = (state: RootState) => state.order.loading;
export const OrderPagination = (state: RootState) => state.order.pagination;
export const OrderTotalPages = (state: RootState) => state.order.totalPages;

